import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import FAQSection from '../components/common/FAQSection';

const BLUE = '#0071e3';
const DARK = '#0f172a';
const DARK2 = '#162033';
const LIGHT = '#f8fafc';

const Icon = ({ type, color = BLUE, size = 42 }) => {
  const id = `caio-${type}-${color.replace('#', '')}`;
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 64 64',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
  };
  const defs = (
    <defs>
      <linearGradient id={`${id}-g`} x1="12" y1="8" x2="52" y2="56" gradientUnits="userSpaceOnUse">
        <stop stopColor={color} />
        <stop offset="1" stopColor="#0f172a" />
      </linearGradient>
      <radialGradient id={`${id}-r`} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(22 18) rotate(48) scale(42)">
        <stop stopColor="#ffffff" stopOpacity="0.92" />
        <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
      </radialGradient>
    </defs>
  );
  const shell = (children) => (
    <svg {...common}>
      {defs}
      <rect x="5" y="5" width="54" height="54" rx="18" fill={`url(#${id}-g)`} />
      <circle cx="22" cy="18" r="24" fill={`url(#${id}-r)`} opacity="0.42" />
      <path d="M13 49c13-1 26-11 38-30" stroke="white" strokeOpacity="0.18" strokeWidth="8" strokeLinecap="round" />
      {children}
    </svg>
  );

  const nodes = {
    compass: shell(
      <>
        <circle cx="32" cy="32" r="15" fill="white" fillOpacity="0.18" stroke="white" strokeOpacity="0.78" strokeWidth="3" />
        <path d="M39 22 34.5 35.5 21 40l4.5-13.5L39 22Z" fill="white" />
        <circle cx="32" cy="32" r="3" fill={color} />
      </>
    ),
    transform: shell(
      <>
        <rect x="15" y="16" width="16" height="16" rx="5" fill="white" fillOpacity="0.94" />
        <rect x="33" y="32" width="16" height="16" rx="5" fill="white" fillOpacity="0.94" />
        <path d="M32 21h10c3 0 5 2 5 5v2" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <path d="m43 24 4 4 4-4" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M32 43H22c-3 0-5-2-5-5v-2" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <path d="m21 40-4-4-4 4" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
    educate: shell(
      <>
        <path d="M14 25 32 16l18 9-18 9-18-9Z" fill="white" />
        <path d="M21 32v8c0 4 5 7 11 7s11-3 11-7v-8" fill="white" fillOpacity="0.32" />
        <path d="M49 26v14" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <circle cx="49" cy="43" r="3" fill="white" />
      </>
    ),
    agent: shell(
      <>
        <rect x="16" y="22" width="32" height="24" rx="9" fill="white" fillOpacity="0.94" />
        <path d="M25 22v-3c0-4 3-7 7-7s7 3 7 7v3" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <circle cx="27" cy="34" r="3" fill={color} />
        <circle cx="37" cy="34" r="3" fill={color} />
        <path d="M27 41h10" stroke={color} strokeWidth="4" strokeLinecap="round" />
      </>
    ),
    shield: shell(
      <>
        <path d="M32 13 48 20v12c0 10-6 17-16 21-10-4-16-11-16-21V20l16-7Z" fill="white" fillOpacity="0.94" />
        <path d="m25 33 5 5 10-12" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
    tools: shell(
      <>
        <path d="M41 15a10 10 0 0 0 8 8L28 44a7 7 0 0 1-10 0 7 7 0 0 1 0-10l21-21Z" fill="white" fillOpacity="0.94" />
        <circle cx="23" cy="39" r="3" fill={color} />
        <path d="m37 39 10 10" stroke="white" strokeWidth="5" strokeLinecap="round" />
      </>
    ),
    people: shell(
      <>
        <circle cx="28" cy="24" r="8" fill="white" />
        <circle cx="42" cy="27" r="6" fill="white" fillOpacity="0.72" />
        <path d="M14 49c2-9 8-14 16-14s14 5 16 14H14Z" fill="white" fillOpacity="0.94" />
        <path d="M37 49c1-6 5-10 11-10 4 0 8 3 10 10H37Z" fill="white" fillOpacity="0.52" />
      </>
    ),
    measure: shell(
      <>
        <rect x="15" y="39" width="8" height="10" rx="3" fill="white" fillOpacity="0.72" />
        <rect x="28" y="28" width="8" height="21" rx="3" fill="white" />
        <rect x="41" y="20" width="8" height="29" rx="3" fill="white" fillOpacity="0.86" />
        <path d="M16 28c9 0 16-4 24-13" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <path d="m38 15 6-1-1 6" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  };

  return nodes[type] || nodes.agent;
};

const iconWrap = (accent = BLUE) => ({
  width: '64px',
  height: '64px',
  borderRadius: '22px',
  display: 'grid',
  placeItems: 'center',
  background: `linear-gradient(145deg, ${accent}18, #ffffff 72%)`,
  border: `1px solid ${accent}24`,
  boxShadow: `0 18px 44px ${accent}1c`,
  flexShrink: 0,
});

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

function formatCurrency(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n)}`;
}

function ROICalculator({ isMobile }) {
  const [teamSize, setTeamSize] = useState(150);
  const [hoursLost, setHoursLost] = useState(8);
  const [avgSalary, setAvgSalary] = useState(80000);
  const [months, setMonths] = useState(6);

  const hourlyRate = avgSalary / 52 / 40;
  const weeklyHoursLost = teamSize * hoursLost;
  const annualHoursLost = weeklyHoursLost * 52;
  const annualCostOfInaction = hourlyRate * annualHoursLost;
  const fcaioMonthly = teamSize < 100 ? 4000 : teamSize < 500 ? 7000 : 12000;
  const fcaioAnnual = fcaioMonthly * 12;
  const roi = annualCostOfInaction / fcaioAnnual;
  const partialYearCost = (annualCostOfInaction / 12) * months;

  const Slider = ({ label, value, min, max, step = 1, onChange, format }) => (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#94a3b8' }}>{label}</span>
        <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'white' }}>{format ? format(value) : value}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: BLUE, cursor: 'pointer' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{format ? format(min) : min}</span>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{format ? format(max) : max}</span>
      </div>
    </div>
  );

  return (
    <section style={{ padding: '80px 0', background: DARK }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <span style={{ color: BLUE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>ROI Calculator</span>
          <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 800, color: 'white', marginTop: '12px', marginBottom: '12px', letterSpacing: '-0.03em' }}>
            The cost of no AI leadership
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '1.05rem', maxWidth: '600px', margin: '0 auto' }}>
            Every month without an AI strategy is productivity, speed, and competitive ground left on the table.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '32px', alignItems: 'start' }}>
          {/* Inputs */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.08)', padding: '32px' }}>
            <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', marginBottom: '28px', marginTop: 0 }}>Your organisation</h3>
            <Slider label="Team size" value={teamSize} min={10} max={2000} step={10} onChange={setTeamSize} />
            <Slider label="Hours/week lost to manual, automatable tasks (per person)" value={hoursLost} min={1} max={25} onChange={setHoursLost} />
            <Slider label="Months without an AI strategy" value={months} min={1} max={24} onChange={setMonths} />
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#94a3b8' }}>Average fully-loaded employee cost/year</span>
                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'white' }}>{formatCurrency(avgSalary)}</span>
              </div>
              <input
                type="range" min={40000} max={300000} step={5000} value={avgSalary}
                onChange={e => setAvgSalary(Number(e.target.value))}
                style={{ width: '100%', accentColor: BLUE, cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>$40K</span>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>$300K</span>
              </div>
            </div>
          </div>

          {/* Outputs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              {
                label: 'Annual cost of unautomated work',
                value: formatCurrency(annualCostOfInaction),
                sub: `${Math.round(annualHoursLost / 1000)}K hours/year across your team`,
                color: '#f87171',
              },
              {
                label: `Cost of ${months}-month AI strategy gap`,
                value: formatCurrency(partialYearCost),
                sub: 'Productivity and speed lost while competitors automate',
                color: '#fbbf24',
              },
              {
                label: 'Estimated fCAIO annual cost',
                value: formatCurrency(fcaioAnnual),
                sub: `~${formatCurrency(fcaioMonthly)}/month retainer for your team size`,
                color: '#34d399',
              },
              {
                label: 'ROI multiple',
                value: `${roi.toFixed(1)}×`,
                sub: 'Return on fCAIO investment vs. cost of inaction',
                color: BLUE,
                big: true,
              },
            ].map(item => (
              <div key={item.label} style={{
                background: item.big ? `linear-gradient(135deg, ${BLUE}20, ${BLUE}08)` : 'rgba(255,255,255,0.04)',
                borderRadius: '20px',
                border: `1px solid ${item.big ? BLUE + '40' : 'rgba(255,255,255,0.08)'}`,
                padding: '20px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
              }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>{item.label}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{item.sub}</div>
                </div>
                <div style={{ fontSize: item.big ? '2rem' : '1.5rem', fontWeight: 800, color: item.color, flexShrink: 0 }}>
                  {item.value}
                </div>
              </div>
            ))}

            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px' }}>
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0, lineHeight: 1.6 }}>
                A full-time Chief AI Officer costs <strong style={{ color: '#94a3b8' }}>$250,000–$400,000/year</strong> in Singapore. An Eagentix Fractional CAIO starts at a fraction of that — with faster time-to-value and zero hiring overhead.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function FractionalCAIO() {
  const isMobile = useIsMobile();

  const deliverables = [
    {
      icon: 'compass',
      color: '#0ea5e9',
      title: 'AI Strategy Operating Plan',
      desc: 'Translate executive ambition into a ranked 90-day AI portfolio: where to automate, where to augment people, what to avoid, and which outcomes justify investment.',
    },
    {
      icon: 'transform',
      color: '#f97316',
      title: 'Transformation Sprints',
      desc: 'Map high-friction operating tasks, remove manual drag, and turn repeatable work into AI-assisted processes teams can use immediately.',
    },
    {
      icon: 'educate',
      color: '#22c55e',
      title: 'Practical AI Education',
      desc: 'Run hands-on sessions where employees learn how to think with AI, pressure-test ideas, build throwaway tools, and apply AI inside their actual job context.',
    },
    {
      icon: 'agent',
      color: '#6366f1',
      title: 'Agent & Automation Buildout',
      desc: 'Design and build AI agents, workflow automations, copilots, and internal tools with your team so the engagement creates working systems, not just slideware.',
    },
    {
      icon: 'shield',
      color: '#14b8a6',
      title: 'Governance, Guardrails & Policy',
      desc: 'Define approval gates, usage standards, data rules, audit trails, and human review paths so AI adoption scales responsibly instead of becoming shadow IT.',
    },
    {
      icon: 'measure',
      color: '#eab308',
      title: 'Value Tracking & Executive Reporting',
      desc: 'Track hours saved, cost avoided, cycle-time improvements, adoption gaps, and next investment decisions in a format leadership can act on every month.',
    },
  ];

  const engagements = [
    {
      model: 'Hourly / Ad-hoc',
      best: 'Unblocking, reviews, decisions',
      desc: 'Get expert input on a specific question, architecture review, or decision. No commitment. Book when you need it.',
      color: '#38bdf8',
    },
    {
      model: 'Daily Sprint',
      best: 'Focused build week or assessment',
      desc: 'Dedicated full-day engagement for AI readiness assessments, agent architecture sessions, or intensive build sprints.',
      color: '#a78bfa',
    },
    {
      model: 'Monthly Retainer',
      best: 'Ongoing AI operating rhythm',
      desc: 'Structured monthly cadence with leadership sessions, sprint priorities, agent builds, enablement workshops, and async availability between sessions.',
      color: BLUE,
    },
    {
      model: 'Project-Based',
      best: 'Defined scope with handover',
      desc: 'Fixed-scope engagement: assess → design → build → enable. Clear deliverables, timeline, and handover to your team.',
      color: '#34d399',
    },
  ];

  const personas = [
    {
      role: 'CIO / CTO',
      problem: 'Board is asking for an AI strategy. No one internal owns it. Hiring a full-time CAIO takes 6 months.',
      outcome: 'Priority portfolio, risk controls, and working agents in weeks — not quarters.',
    },
    {
      role: 'GenAI Lead / AI Champion',
      problem: 'You have buy-in but not enough hands or deep expertise to turn pilots into production.',
      outcome: 'Senior build support, architecture guidance, and an execution partner who has shipped agents before.',
    },
    {
      role: 'Founder / CEO',
      problem: 'You know AI should transform your operations but don\'t have an AI team or know where to start.',
      outcome: 'A practical AI-first strategy, first agents built, and your team upskilled to own it.',
    },
    {
      role: 'Enterprise Team Lead',
      problem: 'Your team is building AI but lacks guardrails, governance, and a coherent architecture across initiatives.',
      outcome: 'Reusable agent patterns, platform standards, and review practices that scale across the organisation.',
    },
  ];

  const steps = [
    {
      n: '01',
      title: 'Assess',
      desc: 'Audit AI maturity, workflows, data assets, tools, and team capability. Identify the work where AI can save time, reduce cost, or create strategic advantage.',
    },
    {
      n: '02',
      title: 'Align',
      desc: 'Turn the assessment into a practical operating plan: priorities, sprint scope, training needs, decision gates, and success metrics.',
    },
    {
      n: '03',
      title: 'Build',
      desc: 'Build agents, automations, prototypes, and repeatable patterns with the team so learning happens through implementation.',
    },
    {
      n: '04',
      title: 'Enable',
      desc: 'Create internal champions, document playbooks, and reduce dependency on outside experts over time.',
    },
  ];

  const vsRows = [
    ['Time to start', '1–2 weeks', '4–6 months', '2–4 weeks'],
    ['Annual cost (SG)', 'Fraction of salary', '$250K–$400K', 'Report only, no execution'],
    ['Hands-on building', '✓ Yes', '✓ Yes', '✗ No'],
    ['Ongoing operating cadence', '✓ Yes', '✓ Yes', '✗ No'],
    ['Model-agnostic', '✓ Yes', 'Varies', 'Varies'],
    ['Team upskilling', '✓ Yes', 'Sometimes', '✗ No'],
    ['Governance setup', '✓ Yes', 'Sometimes', 'Sometimes'],
  ];

  const stePillars = [
    { letter: 'S', name: 'Strategy', point: 'AI maturity and opportunity scan' },
    { letter: 'T', name: 'Transformation', point: 'Workflow redesign and automation sprints' },
    { letter: 'E', name: 'Education', point: 'Role-based AI working sessions' },
  ];

  const trainingPlatforms = [
    {
      name: 'ChatGPT Enterprise',
      desc: 'Secure enterprise prompting, custom GPTs, research, analysis, and team workflows.',
      logo: '/images/icons/openai.svg',
      color: '#10a37f',
    },
    {
      name: 'Claude for Work',
      desc: 'Knowledge work, reasoning, document analysis, coding support, and safe team usage.',
      logo: '/images/icons/anthropic.svg',
      color: '#d97757',
    },
    {
      name: 'Microsoft Copilot',
      desc: 'M365 productivity, meetings, documents, Teams, Excel, PowerPoint, and adoption patterns.',
      logo: '/images/icons/microsoft-copilot.svg',
      color: '#2563eb',
    },
    {
      name: 'Gemini Enterprise',
      desc: 'Google Workspace, research, multimodal workflows, and business process enablement.',
      logo: '/images/icons/google.svg',
      color: '#4285f4',
    },
  ];

  return (
    <>
      <SEO
        title="Fractional Chief AI Officer | STE AI Strategy, Transformation & Education | Eagentix"
        description="Embed a Fractional Chief AI Officer into your team through the STE model: AI strategy, workflow transformation, hands-on education, agent buildout, governance, and measurable value creation."
        pathname="/fractional-caio"
        agentKey="FractionalCAIO"
      />

      {/* HERO */}
      <section style={{ 
        padding: isMobile ? '80px 0 60px' : '112px 0 82px', 
        minHeight: '720px',
        display: 'flex',
        alignItems: 'center',
        background: '#070a12',
        position: 'relative', 
        overflow: 'hidden' 
      }}>
        {/* Background Video (Spinning Earth Mixkit 29351 - High Visibility) */}
        <video 
          autoPlay 
          loop 
          playsInline 
          muted 
          style={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover', 
            transform: 'translate(-50%, -50%)', 
            zIndex: 1, 
            opacity: 0.85 
          }}
        >
          <source src="/videos/earth-bg.mp4" type="video/mp4" />
        </video>

        {/* Crisp Semi-Transparent Overlay for Legibility */}
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: `linear-gradient(160deg, rgba(7, 10, 18, 0.65) 0%, rgba(15, 23, 42, 0.45) 60%, rgba(7, 10, 18, 0.70) 100%)`, 
          zIndex: 2 
        }} />

        {/* Ambient Glow */}
        <div style={{ position: 'absolute', top: '20%', right: '10%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(14,165,233,0.25) 0%, rgba(249,115,22,0.15) 50%, transparent 70%)', pointerEvents: 'none', zIndex: 2 }} />

        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 3, width: '100%' }}>
          {/* Full-width tagline */}
          <div style={{ marginBottom: isMobile ? '40px' : '52px' }}>
            <span style={{ display: 'inline-block', background: `${BLUE}20`, color: BLUE, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1.5px', padding: '6px 14px', borderRadius: '100px', marginBottom: '24px', border: `1px solid ${BLUE}30` }}>
              Fractional Chief AI Officer · STE Model
            </span>
            <h1 style={{ fontSize: isMobile ? '2.4rem' : '4rem', fontWeight: 800, color: 'white', lineHeight: 1.08, letterSpacing: '-0.04em', margin: 0, maxWidth: '900px' }}>
              AI leadership that sits with your people and changes how work gets done.
            </h1>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.5fr) minmax(320px, 1fr)', gap: isMobile ? '44px' : '42px', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: '1.18rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '28px', maxWidth: '640px' }}>
              Eagentix brings a senior AI operator into your business to find expensive manual work, redesign it with AI, and train teams while the change is happening.
            </p>
            <div style={{ position: 'relative', padding: isMobile ? '18px' : '22px', borderRadius: '26px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', marginBottom: '28px', maxWidth: '720px' }}>
              <div style={{ color: '#7dd3fc', fontWeight: 850, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.78rem', marginBottom: '14px' }}>STE Framework</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '12px' }}>
                {stePillars.map((p, index) => (
                  <div key={p.name} style={{ position: 'relative', padding: '18px', borderRadius: '22px', background: '#ffffff', border: '1px solid rgba(148,163,184,0.18)', minHeight: '150px', boxShadow: '0 14px 34px rgba(2,6,23,0.14)' }}>
                    <div style={{ color: '#2563eb', fontSize: '2.15rem', lineHeight: 1, fontWeight: 950, letterSpacing: '-0.06em', marginBottom: '12px' }}>{p.letter}</div>
                    <div style={{ color: DARK, fontWeight: 900, fontSize: '1.02rem', marginBottom: '10px' }}>{p.name}</div>
                    <div style={{ color: '#64748b', fontSize: '0.84rem', lineHeight: 1.5 }}>{p.point}</div>
                    {index < stePillars.length - 1 && !isMobile && (
                      <div style={{ position: 'absolute', right: '-17px', top: '50%', transform: 'translateY(-50%)', width: '24px', height: '24px', borderRadius: '999px', background: '#2563eb', color: 'white', display: 'grid', placeItems: 'center', fontWeight: 900, zIndex: 2 }}>›</div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '10px' }}>
                {[
                  ['Time saved', 'Manual work removed'],
                  ['Cost avoided', 'Waste reduced'],
                  ['Capability built', 'Champions created'],
                ].map(([label, text]) => (
                  <div key={label} style={{ padding: '12px 14px', borderRadius: '16px', background: 'rgba(15,23,42,0.48)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ color: 'white', fontWeight: 850, fontSize: '0.84rem' }}>{label}</div>
                    <div style={{ color: '#94a3b8', fontSize: '0.76rem', marginTop: '2px' }}>{text}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a href="#contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: BLUE, color: 'white', fontWeight: 700, fontSize: '1rem', padding: '14px 28px', borderRadius: '12px', textDecoration: 'none' }}>
                Book a Strategy Session
              </a>
              <a href="#engagements" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.08)', color: 'white', fontWeight: 700, fontSize: '1rem', padding: '14px 28px', borderRadius: '12px', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)' }}>
                See Engagement Models
              </a>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', inset: '-34px -24px auto auto', width: '260px', height: '260px', borderRadius: '999px', background: 'rgba(37,99,235,0.2)', filter: 'blur(16px)' }} />
            <div style={{ position: 'relative', padding: isMobile ? '20px' : '24px', borderRadius: '32px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 28px 80px rgba(0,0,0,0.28)', backdropFilter: 'blur(16px)', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', width: '210px', height: '210px', right: '-72px', top: '-92px', borderRadius: '999px', background: 'rgba(59,130,246,0.18)' }} />
              <div style={{ color: '#7dd3fc', fontWeight: 850, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.78rem', marginBottom: '16px', position: 'relative' }}>
                Training and adoption across the tools your teams already use
              </div>
              <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                {trainingPlatforms.map(platform => (
                  <div key={platform.name} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px', borderRadius: '20px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: 'rgba(255,255,255,0.96)', border: `1px solid ${platform.color}26`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                      <img src={platform.logo} alt={`${platform.name} logo`} style={{ width: '25px', height: '25px', objectFit: 'contain' }} />
                    </div>
                    <div>
                      <div style={{ color: 'white', fontWeight: 850, fontSize: '0.95rem', lineHeight: 1.2 }}>{platform.name}</div>
                      <div style={{ color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.35, marginTop: '4px' }}>{platform.desc.split(',')[0]}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* VISUAL OPERATING MODEL */}
      <section style={{ padding: '84px 0', background: 'white' }}>
        <div style={{ maxWidth: '1120px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '28px', alignItems: 'stretch' }}>
            <div style={{ padding: isMobile ? '26px' : '32px', borderRadius: '30px', background: 'linear-gradient(145deg, #0f172a, #16335f)', color: 'white', overflow: 'hidden', position: 'relative', boxShadow: '0 24px 70px rgba(15,23,42,0.18)' }}>
              <div style={{ position: 'absolute', inset: 'auto -80px -100px auto', width: '260px', height: '260px', borderRadius: '999px', background: 'rgba(56,189,248,0.22)' }} />
              <div style={{ position: 'relative' }}>
                <span style={{ color: '#7dd3fc', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.4px', fontSize: '0.78rem' }}>Value Map</span>
                <h2 style={{ margin: '12px 0 16px', fontSize: isMobile ? '1.8rem' : '2.25rem', lineHeight: 1.12, letterSpacing: '-0.04em', fontWeight: 900, color: '#ffffff' }}>
                  One view of the AI work, from decision to impact.
                </h2>
                <p style={{ color: '#cbd5e1', fontSize: '1rem', lineHeight: 1.7, margin: '0 0 24px' }}>
                  The operating map shows who owns the decision, where the work changes, what systems are involved, and how value is measured.
                </p>

                <div style={{ display: 'grid', gap: '14px' }}>
                  {[
                    { left: 'Leadership', right: 'AI strategy, budget, KPI ownership', icon: 'compass', color: '#38bdf8' },
                    { left: 'Departments', right: 'Workflow redesign and team education', icon: 'people', color: '#22c55e' },
                    { left: 'Systems', right: 'Agents, automations, integrations, guardrails', icon: 'tools', color: '#f97316' },
                    { left: 'Board Pack', right: 'Hours saved, cost avoided, adoption and risk', icon: 'measure', color: '#eab308' },
                  ].map((row) => (
                    <div key={row.left} style={{ display: 'grid', gridTemplateColumns: '46px 0.9fr 1.1fr', gap: '12px', alignItems: 'center', padding: '13px', borderRadius: '18px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
                      <div style={{ width: '46px', height: '46px', borderRadius: '15px', display: 'grid', placeItems: 'center', background: `${row.color}18` }}>
                        <Icon type={row.icon} color={row.color} size={38} />
                      </div>
                      <div style={{ fontWeight: 850, color: 'white', fontSize: '0.94rem' }}>{row.left}</div>
                      <div style={{ color: '#cbd5e1', fontSize: '0.82rem', lineHeight: 1.45 }}>{row.right}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ padding: isMobile ? '24px' : '30px', borderRadius: '30px', background: '#f8fafc', border: '1px solid rgba(148,163,184,0.18)', boxShadow: '0 20px 60px rgba(15,23,42,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '26px' }}>
                <div>
                  <span style={{ color: BLUE, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.4px', fontSize: '0.78rem' }}>Executive View</span>
                  <h3 style={{ margin: '8px 0 0', color: DARK, fontSize: isMobile ? '1.35rem' : '1.65rem', letterSpacing: '-0.03em', lineHeight: 1.15, fontWeight: 900 }}>
                    What leadership sees every month
                  </h3>
                </div>
                <div style={{ ...iconWrap('#0ea5e9'), width: '50px', height: '50px' }}>
                  <Icon type="measure" size={40} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '14px', marginBottom: '20px' }}>
                {[
                  ['132h', 'Manual hours removed', '#0ea5e9'],
                  ['$48K', 'Estimated annual cost avoided', '#22c55e'],
                  ['11', 'AI workflows in motion', '#f97316'],
                  ['82%', 'Team adoption target', '#6366f1'],
                ].map(([value, label, color]) => (
                  <div key={label} style={{ padding: '18px', borderRadius: '20px', background: 'white', border: `1px solid ${color}20` }}>
                    <div style={{ color, fontSize: '1.8rem', lineHeight: 1, fontWeight: 950, letterSpacing: '-0.05em' }}>{value}</div>
                    <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '8px', lineHeight: 1.4 }}>{label}</div>
                  </div>
                ))}
              </div>

              <div style={{ padding: '20px', borderRadius: '22px', background: 'white', border: '1px solid rgba(148,163,184,0.16)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ color: DARK, fontWeight: 850, fontSize: '0.98rem' }}>Transformation pipeline</div>
                  <div style={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 700 }}>Current month</div>
                </div>
                {[
                  ['Finance close assistant', 86, '#0ea5e9'],
                  ['Sales proposal copilot', 68, '#f97316'],
                  ['Ops knowledge agent', 54, '#22c55e'],
                ].map(([label, pct, color]) => (
                  <div key={label} style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', marginBottom: '7px' }}>
                      <span style={{ color: '#334155', fontSize: '0.86rem', fontWeight: 700 }}>{label}</span>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 800 }}>{pct}%</span>
                    </div>
                    <div style={{ height: '9px', borderRadius: '999px', background: '#e2e8f0', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', borderRadius: '999px', background: `linear-gradient(90deg, ${color}, ${color}99)` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <ROICalculator isMobile={isMobile} />

      {/* COMPARISON TABLE */}
      <section style={{ padding: '80px 0', background: LIGHT }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: BLUE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Why Fractional</span>
            <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: 800, color: DARK, marginTop: '12px', letterSpacing: '-0.03em' }}>
              Fractional CAIO vs full-time hire vs consulting
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 2px 16px rgba(15,23,42,0.06)' }}>
              <thead>
                <tr>
                  {['', 'Eagentix fCAIO', 'Full-Time CAIO', 'AI Consultant'].map((h, i) => (
                    <th key={h} style={{ padding: '16px 20px', textAlign: i === 0 ? 'left' : 'center', background: i === 1 ? BLUE : DARK, color: 'white', fontWeight: 700, fontSize: '0.9rem', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vsRows.map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: '1px solid rgba(148,163,184,0.12)', background: ri % 2 === 0 ? 'white' : LIGHT }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ padding: '14px 20px', textAlign: ci === 0 ? 'left' : 'center', fontSize: '0.93rem', fontWeight: ci === 0 ? 700 : 400, color: ci === 0 ? DARK : cell === '✓ Yes' ? '#10b981' : cell === '✗ No' ? '#ef4444' : '#475569', borderRight: ci < 3 ? '1px solid rgba(148,163,184,0.1)' : 'none' }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* WHY THIS ROLE EXISTS */}
      <section style={{ padding: '80px 0', background: 'white' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '48px', alignItems: 'start' }}>
            <div>
              <span style={{ color: BLUE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Why this role exists</span>
              <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: 800, color: DARK, marginTop: '12px', marginBottom: '20px', letterSpacing: '-0.03em', lineHeight: 1.15 }}>
                Most companies do not need more AI noise. They need ownership.
              </h2>
              <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '16px' }}>
                AI adoption stalls when every department experiments separately, tools are bought without a plan, and employees are told to “use AI” without seeing how it applies to their work.
              </p>
              <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '16px' }}>
                A Fractional CAIO gives the initiative a single accountable owner: someone who can talk to the board, understand operations, work with technology teams, and convert ambition into shipped improvements.
              </p>
              <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.8 }}>
                The result is faster decision-making, less wasted tool spend, fewer disconnected pilots, and a practical path to measurable productivity gains.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { q: 'Board pressure', a: 'Leadership wants a credible AI plan, but internal ownership is unclear and hiring a full-time CAIO may be too slow or too expensive.' },
                { q: 'Operational drag', a: 'Teams lose hours to reporting, research, coordination, proposal work, knowledge search, finance tasks, and repeated admin that AI can reduce.' },
                { q: 'Adoption gap', a: 'Employees need practical coaching inside their own workflows, not generic prompts or one-time training slides.' },
              ].map(item => (
                <div key={item.q} style={{ padding: '20px 24px', borderRadius: '16px', background: LIGHT, border: '1px solid rgba(148,163,184,0.16)' }}>
                  <div style={{ fontWeight: 700, color: DARK, marginBottom: '8px', fontSize: '0.97rem' }}>{item.q}</div>
                  <div style={{ color: '#475569', fontSize: '0.93rem', lineHeight: 1.7 }}>{item.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section style={{ padding: '80px 0', background: LIGHT }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: BLUE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>What I bring</span>
            <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: 800, color: DARK, marginTop: '12px', marginBottom: '12px', letterSpacing: '-0.03em' }}>
              Concrete outputs your leadership and teams can use
            </h2>
            <p style={{ color: '#475569', fontSize: '1.04rem', lineHeight: 1.7, maxWidth: '680px', margin: '0 auto' }}>
              Each engagement produces decisions, artifacts, working systems, and enablement material instead of ending at recommendations.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px' }}>
            {deliverables.map(d => (
              <div key={d.title} style={{ padding: '28px', borderRadius: '24px', background: 'white', border: `1px solid ${d.color}22`, boxShadow: '0 16px 40px rgba(15,23,42,0.06)' }}>
                <div style={{ ...iconWrap(d.color), marginBottom: '18px' }}>
                  <Icon type={d.icon} color={d.color} />
                </div>
                <h3 style={{ fontWeight: 800, color: DARK, fontSize: '1.05rem', marginBottom: '10px', marginTop: 0 }}>{d.title}</h3>
                <p style={{ color: '#475569', fontSize: '0.93rem', lineHeight: 1.75, margin: 0 }}>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MONTHLY OPERATING CADENCE */}
      <section style={{ padding: '80px 0', background: DARK2 }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: BLUE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Monthly Retainer</span>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', marginTop: '12px', marginBottom: '12px', letterSpacing: '-0.03em' }}>
              How a month looks inside your team
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto' }}>
              Every monthly retainer follows a structured four-week operating cadence — so AI progress compounds instead of stalling between sessions.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '2px', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
            {[
              {
                week: 'Week 1',
                title: 'Executive Alignment',
                items: [
                  'AI KPI review and roadmap adjustment',
                  'Leadership priority decisions',
                  'Blocker identification and escalation',
                  'Next-sprint scoping',
                ],
                color: BLUE,
              },
              {
                week: 'Week 2',
                title: 'Domain Deep-Work',
                items: [
                  'Embedded work in target department',
                  'Workflow mapping and gap analysis',
                  'Agent blueprint and architecture sessions',
                  'Stakeholder interviews',
                ],
                color: '#a78bfa',
              },
              {
                week: 'Week 3',
                title: 'Build Sprint',
                items: [
                  'Hands-on agent building with your team',
                  'Integration, tooling, and governance setup',
                  'Testing and validation of agent outputs',
                  'Async unblocking throughout the week',
                ],
                color: '#34d399',
              },
              {
                week: 'Week 4',
                title: 'Enablement & Review',
                items: [
                  'Team upskilling and training session',
                  'Sprint retrospective and metrics review',
                  'Next-month roadmap and priority planning',
                  'Documentation and handover artefacts',
                ],
                color: '#fbbf24',
              },
            ].map((w, i) => (
              <div key={w.week} style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.05)', padding: '32px 24px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: w.color, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>{w.week}</div>
                <h3 style={{ fontWeight: 800, color: 'white', fontSize: '1.05rem', marginBottom: '20px', marginTop: 0, lineHeight: 1.3 }}>{w.title}</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {w.items.map(item => (
                    <li key={item} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: w.color, flexShrink: 0, marginTop: '7px' }} />
                      <span style={{ color: '#94a3b8', fontSize: '0.88rem', lineHeight: 1.6 }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', color: '#475569', fontSize: '0.9rem', marginTop: '24px' }}>
            Plus async availability throughout the month for questions, reviews, and unblocking.
          </p>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section id="engagements" style={{ padding: '80px 0', background: 'white' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: BLUE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Engagement Models</span>
            <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: 800, color: DARK, marginTop: '12px', marginBottom: '12px', letterSpacing: '-0.03em' }}>
              Flexible to your need
            </h2>
            <p style={{ color: '#475569', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto' }}>
              No fixed contracts. Choose hourly for ad-hoc input, a monthly retainer for ongoing ownership, or start with a defined project and convert later.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '20px' }}>
            {engagements.map(e => (
              <div key={e.model} style={{ padding: '28px 32px', borderRadius: '20px', background: LIGHT, border: `1px solid ${e.color}30`, boxShadow: `0 4px 24px ${e.color}10` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: e.color, flexShrink: 0 }} />
                  <h3 style={{ fontWeight: 800, color: DARK, fontSize: '1.1rem', margin: 0 }}>{e.model}</h3>
                </div>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: e.color, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Best for: {e.best}</div>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>{e.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <a href="mailto:contact@eagentix.com" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: BLUE, color: 'white', fontWeight: 700, fontSize: '1rem', padding: '14px 32px', borderRadius: '12px', textDecoration: 'none' }}>
              Discuss Your Engagement
            </a>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section style={{ padding: '80px 0', background: LIGHT }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: BLUE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Who it's for</span>
            <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: 800, color: DARK, marginTop: '12px', letterSpacing: '-0.03em' }}>
              Built for leaders who need AI to move
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '20px' }}>
            {personas.map(p => (
              <div key={p.role} style={{ padding: '28px 32px', borderRadius: '20px', background: 'white', border: '1px solid rgba(148,163,184,0.16)' }}>
                <div style={{ display: 'inline-block', background: `${BLUE}12`, color: BLUE, fontWeight: 800, fontSize: '0.85rem', padding: '5px 14px', borderRadius: '100px', marginBottom: '16px' }}>{p.role}</div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>The problem</div>
                  <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{p.problem}</p>
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>The outcome</div>
                  <p style={{ color: '#0f172a', fontSize: '0.95rem', lineHeight: 1.65, margin: 0, fontWeight: 600 }}>{p.outcome}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ padding: '80px 0', background: 'white' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: BLUE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>How it works</span>
            <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: 800, color: DARK, marginTop: '12px', letterSpacing: '-0.03em' }}>
              Assess → Align → Build → Enable
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '20px' }}>
            {steps.map((s, i) => (
              <div key={s.n} style={{ position: 'relative', padding: '28px 24px', borderRadius: '20px', background: LIGHT, border: '1px solid rgba(148,163,184,0.16)' }}>
                <div style={{ fontSize: '2rem', fontWeight: 900, color: `${BLUE}20`, marginBottom: '12px', lineHeight: 1 }}>{s.n}</div>
                <h3 style={{ fontWeight: 800, color: BLUE, fontSize: '1.1rem', marginBottom: '10px', marginTop: 0 }}>{s.title}</h3>
                <p style={{ color: '#475569', fontSize: '0.93rem', lineHeight: 1.75, margin: 0 }}>{s.desc}</p>
                {i < steps.length - 1 && !isMobile && (
                  <div style={{ position: 'absolute', right: '-12px', top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1', fontSize: '1.5rem', zIndex: 1 }}>›</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What the first 30 days produce */}
      <section style={{ background: DARK, padding: '80px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ maxWidth: '850px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)', 
            borderRadius: '24px', 
            padding: '48px', 
            border: `2px solid ${BLUE}`, 
            boxShadow: `0 25px 50px -12px ${BLUE}40` 
          }}>
            <span style={{ color: BLUE, fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              What The First 30 Days Produce
            </span>
            <h3 style={{ fontSize: '2.2rem', fontWeight: 900, color: 'white', letterSpacing: '-0.02em', marginBottom: '16px' }}>
              A board-ready AI roadmap in the first 30 days.
            </h3>
            <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '700px', margin: '0 auto 28px' }}>
              Within the first 30 days the engagement produces a board-ready AI strategy roadmap, an ROI prioritisation matrix, and an initial architecture plan. We agree that scope in writing before the engagement starts, so you know exactly what lands and when.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection agentKey="FractionalCAIO" />

      {/* CTA */}
      <section id="contact" style={{ padding: '80px 0', background: DARK }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.8rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Ready to put AI leadership to work?
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '1.1rem', lineHeight: 1.7, marginBottom: '40px' }}>
            Book a 30-minute strategy session to assess your AI maturity and identify the highest-value starting points for your organisation.
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <a href="mailto:contact@eagentix.com" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: BLUE, color: 'white', fontWeight: 700, fontSize: '1rem', padding: '16px 36px', borderRadius: '12px', textDecoration: 'none' }}>
              Book a Strategy Session
            </a>
            <Link to="/#agents" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.08)', color: 'white', fontWeight: 700, fontSize: '1rem', padding: '16px 36px', borderRadius: '12px', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)' }}>
              View All Agents
            </Link>
          </div>

          {/* P.S. Section (§3.12) */}
          <div style={{ marginTop: '40px', padding: '24px', borderRadius: '16px', background: 'rgba(30, 41, 59, 0.8)', border: `1px dashed ${BLUE}`, textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
              <strong style={{ color: BLUE, fontStyle: 'normal' }}>P.S.</strong> All strategic recommendations and agent architecture plans are built around your existing security, compliance, and cloud governance frameworks.
            </p>
          </div>

          <p style={{ color: '#475569', fontSize: '0.9rem', marginTop: '24px' }}>
            Singapore-based · Available globally · contact@eagentix.com
          </p>
        </div>
      </section>
    </>
  );
}
